<!-- src/views/Functions.vue -->
<template>
  <div style="background-color:black; overflow:hidden;">
    <!-- Loader -->
    <v-container
      v-if="isLoading"
      class="fill-height d-flex align-center justify-center"
    >
      <v-progress-circular indeterminate color="#ff3b47" size="64" />
    </v-container>

    <!-- Main content -->
    <div v-else>
      <!-- Welcome -->
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <v-card-title
              style="color:white; font-size:1.5rem;"
              class="font-weight-bold"
            >
              Welcome to your Nexus Dashboard.
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <!-- Function cards -->
      <div
        class="pa-4"
        style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;"
      >
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div
            class="text-h4 font-weight-bold mx-6 my-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:1px;
                   text-shadow:2px 2px 4px rgba(0,0,0,.2);"
          >
            Functions
          </div>
        </v-row>

        <!-- New function cards with 3 per row design -->
        <div class="functions-grid">
          <div 
            v-for="(category, idx) in filteredCategories"
            :key="idx"
            class="function-card" 
            @click="navigateToCategory(category.name)"
          >
            <div class="function-card-content">
              <div class="function-icon">
                <v-icon :style="{ fontSize: '36px', color: '#4a5568' }">
                  {{ category.icon }}
                </v-icon>
              </div>
              <div class="function-info">
                <h3>{{ category.displayName }}</h3>
                <p>{{ getDescription(category.name) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Access-denied dialog -->
      <v-dialog v-model="dialogVisible" max-width="400">
        <v-card>
          <v-card-title class="headline">Access Denied</v-card-title>
          <v-card-text>
            You do not have access to this section. Please contact support.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn color="red darken-1" text @click="dialogVisible = false"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import {
  getFirestore,
  doc,
  getDoc,
  getDocFromServer,
  getDocFromCache
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);

export default {
  name: "NexusDashboard",
  data() {
    return {
      categories: [
        { name: "jobs",             displayName: "JOBS",               icon: "mdi-briefcase",             bgColor: "#22323D" },
        { name: "oldJobs",          displayName: "OLD JOBS",           icon: "mdi-briefcase-outline",     bgColor: "#22323D" },
        { name: "jobReports",       displayName: "JOB REPORTS",        icon: "mdi-chart-box-outline",     bgColor: "#22323D" },
        { name: "returns",          displayName: "RETURNS",            icon: "mdi-backspace",             bgColor: "#22323D" },
        { name: "deliveries",       displayName: "DELIVERIES",         icon: "mdi-car",                   bgColor: "#22323D" },
        { name: "purchaseOrders",   displayName: "PURCHASE ORDERS",    icon: "mdi-cash",                  bgColor: "#22323D" },
        { name: "engineerQuotes",   displayName: "ENGINEER QUOTES",    icon: "mdi-file-document-outline", bgColor: "#22323D" },
        { name: "salesOrderTracker",displayName: "SALES ORDER TRACKER",icon: "mdi-cart-outline",          bgColor: "#22323D" },
        { name: "awaitingSpares",   displayName: "AWAITING SPARES",    icon: "mdi-clock-outline",         bgColor: "#22323D" },
        { name: "samples",          displayName: "SAMPLES",            icon: "mdi-flask-outline",         bgColor: "#22323D" },
        { name: "shipment",         displayName: "SHIPMENTS",          icon: "mdi-truck-delivery",        bgColor: "#22323D" },
        { name: "serialNumber",     displayName: "SERIAL NUMBERS",     icon: "mdi-numeric",               bgColor: "#22323D" },
        { name: "sharedServices",   displayName: "SHARED SERVICES",    icon: "mdi-account-group",          bgColor: "#22323D" },
        { name: "customerQuotes",  displayName: "CUSTOMER QUOTES",  icon: "mdi-file-document-edit-outline", bgColor: "#22323D" },
        { name: "quoted",          displayName: "QUOTED",            icon: "mdi-file-check-outline",         bgColor: "#22323D" },
        { name: "inventoryView",   displayName: "INVENTORY",         icon: "mdi-package-variant-closed",    bgColor: "#22323D" }
      ],
      userData: {},
      userId: null,
      userEmail: null,
      isLoading: true,
      dialogVisible: false
    };
  },
  computed: {
    filteredCategories() {
      return this.categories.filter(cat => {
        const perm =
          this.userData[cat.name] ||
          (cat.name === "serialNumber" ? this.userData.serial : null);
        return perm && (perm.View_in_Functions || perm.View_In_Function);
      });
    }
  },
  created() {
    this.initAuthWatcher();
  },
  methods: {
    initAuthWatcher() {
      const auth = getAuth(firebaseApp);
      onAuthStateChanged(auth, async user => {
        if (!user) {
          this.$router.push("/login");
          return;
        }

        this.userId = user.uid;
        this.userEmail = user.email;

        await this.fetchUserData();
      });
    },

    async fetchUserData() {
      try {
        const docRef = doc(db, "users", this.userId);
        let docSnap;

        try {
          // Try to get from server first
          docSnap = await getDocFromServer(docRef);
        } catch (serverError) {
          console.log("Server unavailable, trying cache...", serverError);
          
          try {
            // Fallback to cache if offline
            docSnap = await getDocFromCache(docRef);
          } catch (cacheError) {
            console.error("Cache also unavailable:", cacheError);
            
            // Wait a moment and retry once
            await new Promise(resolve => setTimeout(resolve, 1000));
            docSnap = await getDoc(docRef);
          }
        }

        if (docSnap && docSnap.exists()) {
          this.userData = docSnap.data();
        } else {
          console.warn("No user document found");
          this.dialogVisible = true;
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        this.dialogVisible = true;
      } finally {
        this.isLoading = false;
      }
    },

    navigateToCategory(name) {
      const routes = {
        jobs: "/AllJobs",
        jobReports: "/AllJobReports",
        oldJobs: "/OldJobs",
        returns: "/AllReturns",
        deliveries: "/AllDeliveries",
        purchaseOrders: "/AllPO",
        engineerQuotes: "/AllQuote",
        salesOrderTracker: "/AllSalesTracker",
        awaitingSpares: "/AllSpares",
        samples: "/AllSample",
        shipment: "/AllShipment",
        serialNumber: "/AllSerialNumbers",
        sharedServices: "/shared-services",
        customerQuotes: "/AddQuotation",
        quoted: "/AllQuotation",
        inventoryView: "/inventory-view"
      };
      this.$router.push(routes[name] || "/");
    },

    getDescription(categoryName) {
      const descriptions = {
        jobs: "Manage and track recon jobs",
        jobReports: "View and analyze job reports",
        oldJobs: "View and manage old jobs",
        returns: "Process and track all returns",
        deliveries: "Track delivery and collection statuses",
        purchaseOrders: "Create and manage purchase orders",
        engineerQuotes: "Generate engineered quotes",
        salesOrderTracker: "Monitor sales order progress",
        awaitingSpares: "Track pending spare parts",
        samples: "Manage and track all samples",
        shipment: "Track and monitor all shipments",
        serialNumber: "Manage product serial numbers",
        sharedServices: "Project management and shared resources",
        customerQuotes: "Create and send customer quotations",
        quoted: "Manage and approve customer quotes",
        inventoryView: "View and manage inventory costs"
      };
      return descriptions[categoryName] || "Manage this function";
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap");

body,
div,
span,
v-card-title,
v-row,
v-col,
v-card {
  font-family: "Montserrat", sans-serif;
}

.functions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.function-card {
  background: white;
  border-radius: 8px;
  padding: 0;
  transition: all 0.3s ease;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid #e0e6ed;
  position: relative;
}

.function-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: #4a5568;
}

.function-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0,0,0,0.1);
  border-color: #ddd;
}

.function-card-content {
  padding: 30px 25px;
  display: flex;
  align-items: center;
  gap: 15px;
  min-height: 120px;
}

.function-icon {
  min-width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.function-info h3 {
  font-size: 16px;
  color: #2c3e50;
  margin: 0 0 6px 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.function-info p {
  color: #7f8c8d;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .functions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .functions-grid {
    grid-template-columns: 1fr;
  }
  
  .function-card-content {
    padding: 20px;
  }
}

/* Optional: Add focus states for accessibility */
.function-card:focus {
  outline: 2px solid #4a5568;
  outline-offset: 2px;
}
</style>